/**
 * The top bar's Copy button status (owner 2026-10-04): "OFF", "ON · <group>" while the gateway
 * copies one group, or "ON · N groups" while it copies several (ADR-0052). Pure and tiny so it
 * stays in the entry chunk while the copy trader itself loads on demand
 * (apps/web/scripts/check-bundle.mjs).
 */
export type CopyTopBarStatus = {
  /** "OFF", "ON · Main", "ON · 2 groups", "PAUSED · Main", "ERROR". */
  label: string;
  tone: "off" | "on" | "warn";
  title: string;
};

/** One active group as the button reads it (the gateway's `CopyState.groups` entries). */
export type CopyGroupStatus = { groupId: string; name: string; enabled: boolean; configError: string };

/** The fields of the gateway's `CopyState` the button reads (null: no copy state yet). */
export type CopyStatusInput = {
  groupId: string;
  name: string;
  enabled: boolean;
  paused: boolean;
  configError: string;
  /** ADR-0052: every active group; empty from a gateway that knows one group. */
  groups?: readonly CopyGroupStatus[];
} | null;

const nameOf = (g: { name: string }): string => g.name || "group";

export function copyTopBarStatus(s: CopyStatusInput): CopyTopBarStatus {
  if (s === null) return { label: "OFF", tone: "off", title: "Copy trader: no status from the gateway yet" };
  const groups = s.groups ?? [];
  if (groups.length > 0) {
    const on = groups.filter((g) => g.enabled && g.configError === "");
    const broken = groups.filter((g) => g.configError !== "");
    const paused = groups.filter((g) => !g.enabled && g.configError === "");
    const notes = [
      ...broken.map((g) => `${nameOf(g)} cannot run — ${g.configError}`),
      ...(paused.length > 0 ? [`paused: ${paused.map(nameOf).join(", ")}`] : []),
    ];
    const tail = notes.length > 0 ? ` (${notes.join("; ")})` : "";
    if (on.length === 1) {
      const g = on[0] as CopyGroupStatus;
      return {
        label: `ON · ${nameOf(g)}`,
        tone: broken.length > 0 ? "warn" : "on",
        title: `Copy trader: copying ${nameOf(g)}${tail}`,
      };
    }
    if (on.length > 1)
      return {
        label: `ON · ${String(on.length)} groups`,
        tone: broken.length > 0 ? "warn" : "on",
        title: `Copy trader: copying ${on.map(nameOf).join(", ")}${tail}`,
      };
    if (broken.length > 0)
      return { label: "ERROR", tone: "warn", title: `Copy trader: nothing is copied${tail}` };
    return { label: "OFF", tone: "off", title: `Copy trader: off${tail}` };
  }
  if (s.configError !== "")
    return { label: "ERROR", tone: "warn", title: `Copy trader: nothing is copied — ${s.configError}` };
  const group = s.name || "group";
  if (!s.enabled || s.groupId === "")
    return {
      label: "OFF",
      tone: "off",
      title:
        s.groupId === "" ? "Copy trader: off, no group active" : `Copy trader: off (active group ${group})`,
    };
  if (s.paused) return { label: `PAUSED · ${group}`, tone: "warn", title: `Copy trader: ${group} is paused` };
  return { label: `ON · ${group}`, tone: "on", title: `Copy trader: copying ${group}` };
}
